import {
    IVanillaReaderLandmarkData,
    IVanillaReaderNavigationItem,
    IVanillaReaderNavigationTreeData,
    IVanillaReaderReadingProgressionTimeline,
    VanillaReaderPublicationLandmarkType,
} from "./VanillaReaderModel";
import {IAttributeData} from "@colibrio/colibrio-reader-framework/colibrio-core-base";
import {
    ContainerContentBlockType,
    ContentBlockClass,
    IContentBlock,
    IContentBlockMarkData,
    MediaContentBlockType,
    NavigationCollectionType,
    TextContentBlockType
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import {ILocator} from "@colibrio/colibrio-reader-framework/colibrio-core-locator";

export class VanillaReaderLandmark {
    private _attributes: IAttributeData[];
    private _blockClass: ContentBlockClass;
    private _blockType: ContainerContentBlockType | MediaContentBlockType | TextContentBlockType;
    private _children: IContentBlock[];
    private _description: string | undefined;
    private _documentIndex: number | undefined;
    private _id: number;
    private _marks: IContentBlockMarkData[];
    private _resourceHrefs: string[] | null;
    private _textContent: string | undefined;
    private _timelinePosition?: number
    private _serializedData: IVanillaReaderLandmarkData | undefined;
    private _navigationItem: IVanillaReaderNavigationItem | undefined;
    private _locator: ILocator | undefined;
    private _locatorUrl: string | undefined;
    private _subType: VanillaReaderPublicationLandmarkType | undefined;

    constructor(private _contentBlock: IContentBlock, private _navigationTree?: IVanillaReaderNavigationTreeData, private _timeline?: IVanillaReaderReadingProgressionTimeline) {
        let description = this._getDescriptionAttributeFromContentBlock(this._contentBlock, 'aria-label') || this._getDescriptionFromContentBlockStructure(this._contentBlock) || undefined;
        let documentsArray = this._contentBlock.getContentLocation().getReaderDocuments();
        let documentIndex = documentsArray.length > 0 ? documentsArray[0].getIndexInSpine() : undefined;

        this._textContent = this._contentBlock.getTextContent();
        this._id = this._contentBlock.getId();
        this._documentIndex = documentIndex;
        this._resourceHrefs = this._contentBlock.getResourceHrefs();
        this._attributes = this._contentBlock.getAttributes();
        this._marks = this._contentBlock.getMarks();
        this._description = description;
        this._children = this._contentBlock.getChildren();
        this._blockType = this._contentBlock.getBlockType();
        this._blockClass = this._contentBlock.getBlockClass();

        // Let's see if this ContentBlock is also a footnote or endnote

        let hasFootnoteAttribute = this.attributes.some((attribute: IAttributeData) => {
            return (attribute.localName === 'role' && attribute.value === 'doc-footnote')
                || (attribute.localName === 'role' && attribute.value === 'doc-endnote')
                || (attribute.localName === 'type' && attribute.value === 'footnote')
                || (attribute.localName === 'type' && attribute.value === 'endnote')
                || (attribute.localName === 'type' && attribute.value === 'rearnote');

        });

        if (hasFootnoteAttribute) {
            this.subType = VanillaReaderPublicationLandmarkType.FOOTNOTE
        }
    }

    public get id(): number {
        return this._id;
    };

    public get attributes(): IAttributeData[] {
        return this._attributes;
    };

    public get blockClass(): ContentBlockClass {
        return this._blockClass;
    }

    public get blockType(): ContainerContentBlockType | MediaContentBlockType | TextContentBlockType {
        return this._blockType;
    }

    public get children(): IContentBlock[] {
        return this._children;
    }

    public get description(): string | undefined {
        return this._description;
    }

    public get documentIndex(): number | undefined {
        return this._documentIndex;
    }

    public get marks(): IContentBlockMarkData[] {
        return this._marks;
    }

    public get resourceHrefs(): string[] | null {
        return this._resourceHrefs;
    }

    public get subType(): VanillaReaderPublicationLandmarkType | undefined {
        return this._subType;
    }

    public set subType(subType: VanillaReaderPublicationLandmarkType | undefined) {
        this._subType = subType;
    }

    public get textContent(): string | undefined {
        return this._textContent;
    }


    public get locatorUrl(): string | undefined {

        if(this._locatorUrl !== undefined) {
            return this._locatorUrl;
        }
        this._locatorUrl = this._contentBlock.getLocator().toUrl().toString();
        return this._locatorUrl;
    }


    getLocator(charOffset?: number, charLength?:number): ILocator {

        if(this._locator !== undefined) {
            return this._locator;
        }

        this._locator = this._contentBlock.getLocator(charOffset, charLength);

        return this._locator;
    }

    async fetchTimelinePosition(): Promise<number| undefined> {

        if(this._timelinePosition !== undefined) {
            return this._timelinePosition;
        }

        this._timelinePosition = await this._timeline?.fetchTimelinePosition(this._contentBlock.getLocator());
        return this._timelinePosition;
    }

    fetchNavigationItem(): Promise<IVanillaReaderNavigationItem | undefined> {
        return this._fetchNavigationItem();
    }

    isBefore(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().isBefore(contentBlock.getContentLocation());
    }

    isAfter(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().isAfter(contentBlock.getContentLocation());
    }

    contains(contentBlock: IContentBlock): boolean {
        return this._contentBlock.getContentLocation().contains(contentBlock.getContentLocation());
    }

    async serialize(createLocators?: boolean, recursive?: boolean): Promise<IVanillaReaderLandmarkData> {

        if(this._serializedData !== undefined) {
            return this._serializedData;
        }

        const contentBlockData =  this._contentBlock.toSerializableData({createLocators, recursive});
        this._serializedData = {
            timelinePosition: (await this.fetchTimelinePosition()),
            parentId: contentBlockData.parentId,
            id: this.id,
            attributes: this.attributes,
            marks: this.marks,
            resourceHrefs: this.resourceHrefs,
            description: this.description,
            navigationItem: (await this.fetchNavigationItem()),
            children: contentBlockData.children,
            locator: this.getLocator(),
            textContent: this.textContent || '',
            blockClass: this.blockClass,
            blockType: this.blockType,
            subType: this.subType,
            documentIndex: this.documentIndex,
            locatorUrl: this.getLocator().toUrl().toString(),
        };

        return this._serializedData;
    }

    private async _fetchNavigationItem(): Promise<IVanillaReaderNavigationItem | undefined> {

        if(this._navigationItem !== undefined) {
            return this._navigationItem;
        }

        let colibrioNavigationItemRefs = await this._contentBlock.getContentLocation().fetchNavigationItemReferences({
            collectionTypes: [NavigationCollectionType.TOC],
            greedy: true
        });
        let navigationItem: IVanillaReaderNavigationItem | undefined = undefined;

        if(colibrioNavigationItemRefs.getItemsInRange().length > 0) {
            let locator: string | undefined = colibrioNavigationItemRefs.getItemsInRange()[0].getNavigationItem().getLocator()?.toUrl().toString();
            if(locator) {
                navigationItem = this._navigationTree?.items.find((item:  IVanillaReaderNavigationItem)=>{
                    return item.locator === locator;
                });
            }
        }

        this._navigationItem = navigationItem;

        return navigationItem;
    }

    private _getDescriptionFromContentBlockStructure(block: IContentBlock): string | undefined {
        let childTextBlock = block.getChildren().filter(block => block.getBlockType() === TextContentBlockType.TEXT);
        if(childTextBlock.length === 0) {
            return;
        }
        return childTextBlock.map(block => block.getTextContent()).join(' ').trim();
    }

    private _getDescriptionAttributeFromContentBlock(
        block: IContentBlock,
        preferredAttributeName: string | undefined = undefined
    ): string | undefined {
        let blockAttributes = block.getAttributes();
        let descAttribute: string | undefined = undefined;

        if (preferredAttributeName) {
            let attr = blockAttributes.find((attr: IAttributeData) => {
                return attr.localName == preferredAttributeName;
            });
            if (attr) {
                return attr.value;
            }
        }

        blockAttributes.forEach((attr: IAttributeData) => {
            switch (attr.localName) {
                case 'title':
                    descAttribute = attr.value;
                    break;
                case 'alt':
                    descAttribute = attr.value;
                    break;
                case 'aria-label':
                    descAttribute = attr.value;
                    break;
            }
        });

        return descAttribute;
    }

}
